import { Inject, Injectable, InjectionToken } from '@angular/core';
import { HttpErrorResponse } from '@angular/common/http';
import { Observable, catchError, map, of, shareReplay, tap, timeout } from 'rxjs';
import { AuthService } from 'app/core/auth/auth.service';
import { UserService as UserApiService } from 'app/shared/apis/user/api/user.service';
import { Persona, PersonaUser, resolvePersona } from 'app/core/auth/persona-mapping';

export interface ResolvedPersona {
    persona: Persona;
    superAdmin: boolean;
    /** Holds a qualifying (non-Pending, known-role) entry in the selected workspace. */
    member: boolean;
    /** SuperAdmin role on a non-Pending entry of the SELECTED workspace (not merely anywhere). */
    superAdminHere: boolean;
    /** User/Me failed (error, timeout, empty) and the result comes from the stored user. Guards must allow: the backend is the authority. */
    failed: boolean;
    /** User/Me answered 401: guards must NOT activate; the interceptor signs out and goes to /sign-in. */
    unauthorized: boolean;
}

/** How long a `none` from a SUCCESSFUL User/Me is cached (negative cache). Overridable in tests. */
export const PERSONA_NEGATIVE_TTL_MS = new InjectionToken<number>('PERSONA_NEGATIVE_TTL_MS', {
    providedIn: 'root',
    factory: () => 15000,
});

/** How long a User/Me FAILURE (fallback to the stored user) is cached. Short: the stored user may be stale. */
export const PERSONA_FAILURE_TTL_MS = new InjectionToken<number>('PERSONA_FAILURE_TTL_MS', {
    providedIn: 'root',
    factory: () => 3000,
});

/** Upper bound for a User/Me call; a hung call takes the failure path instead of stalling navigation. */
export const PERSONA_USERME_TIMEOUT_MS = new InjectionToken<number>('PERSONA_USERME_TIMEOUT_MS', {
    providedIn: 'root',
    factory: () => 10000,
});

/** How long a positive persona is cached, so a server-side promotion/demotion is picked up without sign-out. */
export const PERSONA_POSITIVE_TTL_MS = new InjectionToken<number>('PERSONA_POSITIVE_TTL_MS', {
    providedIn: 'root',
    factory: () => 5 * 60 * 1000,
});

/** Comparable fingerprint of what the persona depends on: tenant id, status and roles. */
const basisOf = (user: any): string =>
    JSON.stringify(
        (user?.tenants ?? [])
            .map((t: any) => [t?.id ?? '', String(t?.invitationStatus ?? ''), [...(t?.roles ?? [])].map(String).sort()])
            .sort()
    );

/**
 * SPEC-web-planner-007 AC-17: persona for the selected workspace, taken from
 * User/Me. User/Me is called ONCE per (signed-in user, selected workspace) and
 * the result is shared.
 *
 * The cache is dropped by refresh(), AuthService.selectedTenant$ (workspace
 * switch), AuthService.signedOut$, and AuthService.user$:
 *  - while the first User/Me is still in flight (no basis yet) a user$ emit does
 *    not invalidate (it would only duplicate the call), unless it is plainly a
 *    different user (both ids known and different);
 *  - once resolved, a different user, or the same user whose tenants/roles differ
 *    from the cache basis (a demotion patched in by settings), invalidates;
 *  - a failure-derived entry (stored-user fallback) is invalidated by ANY emit.
 * The cache key (user id + workspace id) is a backstop.
 *
 * Expiry: a positive persona lives PERSONA_POSITIVE_TTL_MS (5 min) so a
 * server-side promotion or demotion is picked up without sign-out; a `none` from a
 * successful User/Me lives PERSONA_NEGATIVE_TTL_MS (15 s, wizard step 1 before the
 * async Owner attach); a failed User/Me lives PERSONA_FAILURE_TTL_MS (3 s).
 *
 * The user id used in the key comes from the stored user; when none is stored
 * yet (sign-in ordering), the id from the User/Me response is used.
 */
@Injectable({ providedIn: 'root' })
export class PersonaService {
    private _key: string | null = null;
    private _userId: string | null = null;
    private _basis: string | null = null;
    private _expiresAt = 0; // 0 = still resolving (no expiry yet)
    private _fromFailure = false; // the single failure flag; set from the resolved result
    private _emitBasisDuringFlight: string | null = null; // latest same-user emit basis seen while resolving
    private _cached$: Observable<ResolvedPersona> | null = null;

    constructor(
        private _authService: AuthService,
        private _userApi: UserApiService,
        @Inject(PERSONA_NEGATIVE_TTL_MS) private _negativeTtlMs: number,
        @Inject(PERSONA_FAILURE_TTL_MS) private _failureTtlMs: number,
        @Inject(PERSONA_POSITIVE_TTL_MS) private _positiveTtlMs: number,
        @Inject(PERSONA_USERME_TIMEOUT_MS) private _userMeTimeoutMs: number
    ) {
        this._authService.selectedTenant$?.subscribe(() => this.refresh());
        this._authService.signedOut$?.subscribe(() => this.refresh());
        this._authService.user$?.subscribe((u: any) => this._onUser(u));
    }

    private _onUser(u: any): void {
        if (!this._cached$) {
            return; // nothing cached, nothing to invalidate
        }
        if (this._fromFailure) {
            this.refresh(); // built from a possibly stale stored user
            return;
        }
        const id = u?.id ?? u?.uId ?? '';
        if (this._basis === null) {
            // first User/Me still in flight: only a plainly different user invalidates now;
            // for the same user remember the latest basis and compare when the response lands
            if (id && this._userId && id !== this._userId) {
                this.refresh();
            } else {
                this._emitBasisDuringFlight = basisOf(u);
            }
            return;
        }
        if (id !== (this._userId ?? '') || basisOf(u) !== this._basis) {
            this.refresh();
        }
    }

    /** Drop the cache; the next resolve() calls User/Me again. */
    refresh(): void {
        this._key = null;
        this._userId = null;
        this._basis = null;
        this._expiresAt = 0;
        this._fromFailure = false;
        this._emitBasisDuringFlight = null;
        this._cached$ = null;
    }

    /** Shared observable of the resolved persona (one User/Me per key). */
    resolve(): Observable<ResolvedPersona> {
        const workspaceId = this._authService.selectedTenant?.id ?? '';
        const stored: any = this._authService.user;
        const storedId = stored?.id ?? stored?.uId ?? '';
        const key = `${storedId || this._userId || ''}|${workspaceId}`;
        const expired = this._expiresAt > 0 && Date.now() >= this._expiresAt;
        if (!this._cached$ || this._key !== key || expired) {
            this._key = key;
            this._userId = storedId || this._userId || null;
            this._basis = null;
            this._expiresAt = 0;
            this._fromFailure = false;
            this._emitBasisDuringFlight = null;
            const mine: Observable<ResolvedPersona> = (this._cached$ = this._userApi.userMeGet().pipe(
                timeout({ first: this._userMeTimeoutMs }), // a hung call takes the failure path
                map((resp) => {
                    if (!resp?.isSuccess || !resp.result) {
                        throw new Error('User/Me returned no user');
                    }
                    return { user: resp.result as PersonaUser, failed: false, unauthorized: false };
                }),
                catchError((err) => {
                    console.warn('[PERSONA] User/Me failed; falling back to the cached user', err);
                    const unauthorized = err instanceof HttpErrorResponse && err.status === 401;
                    return of({ user: this._authService.user as PersonaUser | undefined, failed: true, unauthorized });
                }),
                tap((s) => {
                    if (this._cached$ !== mine) {
                        return;
                    }
                    // authoritative id from User/Me; basis from User/Me or, on failure, the stored user
                    const u: any = s.user;
                    const id = s.failed ? '' : (u?.id ?? u?.uId ?? '');
                    if (id) {
                        this._userId = id;
                        this._key = `${id}|${workspaceId}`;
                    }
                    this._basis = basisOf(s.user);
                    this._fromFailure = s.failed; // the one failure flag
                }),
                map((s) => ({ ...resolvePersona(s.user, workspaceId), failed: s.failed, unauthorized: s.unauthorized })),
                tap((r) => {
                    if (this._cached$ !== mine) {
                        return;
                    }
                    if (r.unauthorized) {
                        this.refresh(); // never cache a 401: sign-out is in progress
                        return;
                    }
                    const ttl = r.failed ? this._failureTtlMs : r.persona === null ? this._negativeTtlMs : this._positiveTtlMs;
                    this._expiresAt = Date.now() + ttl;
                    // a same-user emit during flight carried a different basis than this response: stale, refetch next time
                    if (this._emitBasisDuringFlight !== null && this._emitBasisDuringFlight !== this._basis) {
                        this._expiresAt = Date.now();
                    }
                }),
                shareReplay({ bufferSize: 1, refCount: false })
            ));
        }
        return this._cached$;
    }
}
